<template>
  <div class="floating-ai">
    <!-- 主按钮 -->
    <div class="ai-trigger" @click="toggleChat">
      <div class="ai-avatar">
        <svg class="ai-icon" viewBox="0 0 24 24" width="32" height="32">
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" fill="currentColor"/>
          <circle cx="8.5" cy="9.5" r="1.5" fill="currentColor"/>
          <circle cx="15.5" cy="9.5" r="1.5" fill="currentColor"/>
          <path d="M12 17c2.5 0 4.5-1.5 5-3.5H7c.5 2 2.5 3.5 5 3.5z" fill="currentColor" opacity="0.6"/>
        </svg>
      </div>
      <span class="trigger-text">AI客服</span>
      <div class="pulse-ring"></div>
    </div>

    <!-- 弹出对话框 -->
    <transition name="slide-fade">
      <div v-show="showChat" class="chat-popup">
        <div class="popup-header">
          <h3>合利AI客服</h3>
          <button class="close-btn" @click="toggleChat">×</button>
        </div>
        
        <div class="popup-body">
          <!-- 聊天消息区域 -->
          <div class="chat-messages" ref="chatMessages">
            <div v-for="(msg, index) in chatHistory" :key="index" class="message-item">
              <div class="message-avatar" :class="msg.role">
                <span v-if="msg.role === 'user'">👤</span>
                <svg v-else viewBox="0 0 24 24" width="20" height="20">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" fill="currentColor"/>
                  <circle cx="8.5" cy="9.5" r="1.5" fill="currentColor"/>
                  <circle cx="15.5" cy="9.5" r="1.5" fill="currentColor"/>
                  <path d="M12 17c2.5 0 4.5-1.5 5-3.5H7c.5 2 2.5 3.5 5 3.5z" fill="currentColor" opacity="0.6"/>
                </svg>
              </div>
              <div class="message-content" :class="msg.role">
                {{ msg.content }}
              </div>
            </div>
            
            <!-- 当前正在输入的消息 -->
            <div v-if="currentAnswer || isSending" class="message-item">
              <div class="message-avatar ai">
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" fill="currentColor"/>
                  <circle cx="8.5" cy="9.5" r="1.5" fill="currentColor"/>
                  <circle cx="15.5" cy="9.5" r="1.5" fill="currentColor"/>
                  <path d="M12 17c2.5 0 4.5-1.5 5-3.5H7c.5 2 2.5 3.5 5 3.5z" fill="currentColor" opacity="0.6"/>
                </svg>
              </div>
              <div class="message-content ai">
                <span v-if="currentAnswer">{{ currentAnswer }}</span>
                <div v-else-if="isSending" class="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
            
            <!-- 空状态提示 -->
            <div v-if="chatHistory.length === 0 && !currentAnswer" class="empty-state">
              <p>您好！我是合利AI客服，欢迎您！</p>
              <p>有什么可以帮您的吗？</p>
            </div>
          </div>
          
          <!-- 输入区域 -->
          <div class="chat-input-area">
            <textarea 
              v-model="inputMessage" 
              placeholder="输入您的问题..."
              rows="3"
              class="chat-input"
              @keyup.enter="sendMessage"
              :disabled="isSending"
            ></textarea>
            <div class="input-actions">
              <button @click="sendMessage" :disabled="isSending || !inputMessage.trim()" class="send-btn">
                {{ isSending ? '思考中...' : '发送' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'FloatingAI',
  data() {
    return {
      showChat: false,
      inputMessage: '',
      chatHistory: [],
      currentAnswer: '',
      isSending: false,
      conversationId: ''
    }
  },
  methods: {
    toggleChat() {
      this.showChat = !this.showChat
    },
    
    async sendMessage() {
      if (!this.inputMessage.trim() || this.isSending) return
      
      const question = this.inputMessage.trim()
      this.inputMessage = ''
      
      // 添加用户消息到历史
      this.chatHistory.push({
        role: 'user',
        content: question,
        time: new Date().toLocaleTimeString()
      })
      
      this.isSending = true
      this.currentAnswer = ''
      
      // 滚动到底部
      this.$nextTick(() => {
        this.scrollToBottom()
      })
      
      try {
        const requestData = {
          query: question,
          user: 'website-user-' + Date.now(),
          inputs: {},
          conversation_id: this.conversationId
        }
        
        const response = await fetch('/api/ai/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestData)
        })
        
        const result = await response.json()
        
        if (result.error) {
          this.currentAnswer = '抱歉，' + result.error
        } else {
          this.currentAnswer = result.answer || ''
          
          if (result.conversation_id) {
            this.conversationId = result.conversation_id
          }
          
          // 将完整回答添加到历史
          this.chatHistory.push({
            role: 'ai',
            content: this.currentAnswer,
            time: new Date().toLocaleTimeString()
          })
          
          this.currentAnswer = ''
        }
      } catch (error) {
        console.error('对话失败:', error)
        this.currentAnswer = '抱歉，连接失败，请稍后重试。'
      } finally {
        this.isSending = false
        
        // 滚动到底部
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      }
    },
    
    clearChat() {
      this.chatHistory = []
      this.currentAnswer = ''
      this.conversationId = ''
      this.inputMessage = ''
    },
    
    scrollToBottom() {
      if (this.$refs.chatMessages) {
        this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight
      }
    }
  }
}
</script>

<style scoped>
.floating-ai {
  position: fixed;
  right: 30px;
  bottom: 220px;
  z-index: 999;
}

.ai-trigger {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4), 0 0 0 0 rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
  color: white;
  position: relative;
}

.ai-trigger:hover {
  transform: scale(1.1) translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.6), 0 0 0 10px rgba(102, 126, 234, 0.1);
}

.ai-avatar {
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2px;
  backdrop-filter: blur(10px);
}

.ai-icon {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}

.trigger-text {
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
}

/* 脉冲环动画 */
.pulse-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 2px solid rgba(102, 126, 234, 0.5);
  animation: pulse 2s infinite;
  pointer-events: none;
}

@keyframes pulse {
  0% {
    width: 100%;
    height: 100%;
    opacity: 1;
  }
  100% {
    width: 160%;
    height: 160%;
    opacity: 0;
  }
}

.chat-popup {
  position: absolute;
  right: 75px;
  bottom: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  width: 450px;
  max-height: 650px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.popup-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 18px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
}

.popup-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  line-height: 30px;
  text-align: center;
  border-radius: 50%;
  transition: background 0.3s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.popup-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  max-height: 570px;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f5f7fa;
}

.empty-state {
  text-align: center;
  color: #999;
  padding: 40px 20px;
}

.empty-state p {
  margin: 8px 0;
  font-size: 14px;
}

.message-item {
  margin-bottom: 20px;
  display: flex;
  gap: 12px;
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.message-avatar.user {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.message-avatar.ai {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
}

.message-content {
  flex: 1;
  padding: 14px 18px;
  border-radius: 16px;
  font-size: 15px;
  line-height: 1.7;
  word-break: break-word;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.message-content.user {
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  color: #4338ca;
  border-bottom-right-radius: 4px;
}

.message-content.ai {
  background: white;
  color: #1f2937;
  border: 1px solid #e5e7eb;
  border-bottom-left-radius: 4px;
}

.chat-input-area {
  border-top: 1px solid #e5e7eb;
  padding: 18px;
  background: linear-gradient(to bottom, #fafafa, #ffffff);
}

.chat-input {
  width: 100%;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 14px;
  resize: none;
  font-family: inherit;
  transition: all 0.3s;
  background: white;
}

.chat-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.chat-input:disabled {
  background: #f9fafb;
  cursor: not-allowed;
}

.input-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}

.send-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 10px 28px;
  border-radius: 10px;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.send-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
}

.send-btn:active:not(:disabled) {
  transform: translateY(0);
}

.send-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 打字机指示器 */
.typing-indicator {
  display: flex;
  gap: 8px;
  padding: 12px 0;
}

.typing-indicator span {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  animation: typing 1.4s infinite ease-in-out both;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
}

.typing-indicator span:nth-child(1) {
  animation-delay: -0.32s;
}

.typing-indicator span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes typing {
  0%, 80%, 100% {
    transform: scale(0.6);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* 动画效果 */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

/* 响应式 */
@media (max-width: 768px) {
  .floating-ai {
    right: 15px;
    bottom: 220px;
  }

  .chat-popup {
    right: 70px;
    width: 340px;
    max-height: 550px;
  }
  
  .popup-body {
    max-height: 470px;
  }
}
</style>
